import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  Explainer,
  Flex,
  OneHalfLayout,
  PageWrapper,
  Panel,
  Placeholder,
  Button,
  Label,
  TextInput,
  useNotification,
  Span
} from '@ubisend/pulse-components';
import {
  useQuery,
  useLanguages,
  LanguagesConsumer,
  useMutation,
  useQueryClient
} from '@ubisend/pulse-hooks';
import {
  PolicyAccept,
  BackButton
} from '@ubisend/pulse-channels';
import { useCopyToClipboard } from '@ubisend/pulse-component-hooks';

import {
  acceptPolicy as acceptPolicyApi,
  updateSettings as updateSettingsApi
} from '../api';

const configObjectToPairs = (obj = {}) =>
  Object.entries(obj).map(([key, value]) => ({
    key,
    value: String(value)
  }));

const configPairsToObject = (pairs = []) =>
  pairs.reduce((acc, { key, value }) => {
    if (key) { acc[key] = value; }
    return acc;
  }, {});

const VoiceCallDataForm = ({
  url,
  transfer_from,
  voice_settings,
  has_not_api_key
}) => {
  const { languages } = useLanguages();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { copy, copied } = useCopyToClipboard();

  const [activeLanguage, setActiveLanguage] = useState(null);
  const [transferFrom, setTransferFrom] = useState('');
  const [voiceSettingsByLang, setVoiceSettingsByLang] = useState({});
  const [configPairs, setConfigPairs] = useState([]);

  const [showApiKey, setShowApiKey] = useState(has_not_api_key);
  const [apiKey, setApiKey] = useState('');
  const [publicKey, setPublicKey] = useState('');

  const lastLangKeyRef = useRef(null);

  useEffect(() => {
    setTransferFrom(transfer_from || '');

    try {
      const parsed =
        typeof voice_settings === 'string'
          ? JSON.parse(voice_settings)
          : voice_settings || {};

      Object.keys(parsed).forEach(key => {
        parsed[key].config =
          typeof parsed[key].config === 'object' && parsed[key].config !== null
            ? parsed[key].config
            : {};
      });

      setVoiceSettingsByLang(parsed);
    } catch {
      setVoiceSettingsByLang({});
    }
  }, [transfer_from, voice_settings]);

  useEffect(() => {
    if (!activeLanguage && languages?.length) {
      setActiveLanguage(languages[0]);
    }
  }, [languages, activeLanguage]);

  const currentLangKey = activeLanguage?.id;

  const currentLangSettings = voiceSettingsByLang[currentLangKey] || {
    voice: '',
    config: {}
  };

  useEffect(() => {
    if (!currentLangKey) { return; }

    // Only sync when language actually changes
    if (lastLangKeyRef.current !== currentLangKey) {
      const config =
        voiceSettingsByLang?.[currentLangKey]?.config || {};

      setConfigPairs(configObjectToPairs(config));
      lastLangKeyRef.current = currentLangKey;
    }
  }, [currentLangKey, voiceSettingsByLang]);


  const updateCurrentLangSettings = patch => {
    setVoiceSettingsByLang(prev => ({
      ...prev,
      [currentLangKey]: {
        ...prev[currentLangKey],
        ...patch
      }
    }));
  };

  const updateConfigPairs = nextPairs => {
    setConfigPairs(nextPairs);
    updateCurrentLangSettings({
      config: configPairsToObject(nextPairs)
    });
  };

  const missingVoiceLanguages = useMemo(() => {
    if (!languages?.length) { return []; }

    return languages
      .filter(lang => !voiceSettingsByLang?.[lang.id]?.voice)
      .map(lang => lang.name);
  }, [languages, voiceSettingsByLang]);

  const isFormInvalid =
    !transferFrom ||
    missingVoiceLanguages.length > 0 ||
    (showApiKey && (!apiKey || !publicKey));

  const { mutate: updateSettings, isLoading } = useMutation({
    mutationFn: updateSettingsApi,
    onSuccess: () => {
      showSuccess('Successfully updated voice call settings');
      queryClient.invalidateQueries({ queryKey: ['voice-call/settings'] });
    }
  });

  const handleSubmit = () => {
    if (missingVoiceLanguages.length > 0) {
      showError(`Voice is required for: ${missingVoiceLanguages.join(', ')}`);
      return;
    }

    const payload = {
      transfer_from: transferFrom,
      voice_settings: voiceSettingsByLang
    };

    if (showApiKey) {
      payload.api_key = apiKey;
      payload.public_key = publicKey;
    }

    updateSettings(payload);
  };

  return (
    <Flex col>
      <Flex col>
        <Label>API endpoint</Label>
        <Flex>
          <TextInput disabled value={url} />
          <Button icon="clipBoard" onClick={() => copy(url)}>
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </Flex>
      </Flex>

      <Flex col mt>
        {showApiKey ? (
          <>
            <Label>API Key</Label>
            <TextInput value={apiKey} onChange={e => setApiKey(e.target.value)} />
            <Label mt>Public Key</Label>
            <TextInput value={publicKey} onChange={e => setPublicKey(e.target.value)} />
          </>
        ) : (
          <Flex between center>
            <Span>API credentials are already configured</Span>
            <Button onClick={() => setShowApiKey(true)}>
              Change Credentials
            </Button>
          </Flex>
        )}
      </Flex>

      <Flex col mt>
        <Label>Transfer From</Label>
        <TextInput
          value={transferFrom}
          onChange={e => setTransferFrom(e.target.value)}
        />
      </Flex>

      <LanguagesConsumer>
        {missingVoiceLanguages.length > 0 && (
          <Span mt color="danger">
            Missing voice for: {missingVoiceLanguages.join(', ')}
          </Span>
        )}
        <Flex mt>
          {languages.map(lang => (
            <Button
              key={lang.id}
              variant={lang.id === currentLangKey ? 'primary' : 'default'}
              onClick={() => setActiveLanguage(lang)}
            >
              {lang.name}
            </Button>
          ))}
        </Flex>
      </LanguagesConsumer>

      <Flex col mt>
        <Label>Voice</Label>
        <TextInput
          value={currentLangSettings.voice || ''}
          onChange={e => updateCurrentLangSettings({ voice: e.target.value })}
        />
      </Flex>

      <Flex col mt>
        <Label>Voice Config</Label>

        {configPairs.map((pair, index) => (
          <Flex key={index} mb center>
            <TextInput
              placeholder="key"
              value={pair.key}
              onChange={e => {
                const next = [...configPairs];
                next[index] = { ...pair, key: e.target.value };
                updateConfigPairs(next);
              }}
            />
            <TextInput
              placeholder="value"
              value={pair.value}
              onChange={e => {
                const next = [...configPairs];
                next[index] = { ...pair, value: e.target.value };
                updateConfigPairs(next);
              }}
            />
            <Button
              icon="trash"
              colour="danger"
              onClick={() => {
                const next = configPairs.filter((_, i) => i !== index);
                updateConfigPairs(next);
              }}
            />
          </Flex>
        ))}

        <Flex right>
          <Button
            variant="secondary"
            onClick={() =>
              updateConfigPairs([...configPairs, { key: '', value: '' }])
            }
          >
            + Add config
          </Button>
        </Flex>
      </Flex>

      <Flex right mt>
        <Button
          variant="primary"
          disabled={isLoading || isFormInvalid}
          onClick={handleSubmit}
        >
          Save
        </Button>
      </Flex>
    </Flex>
  );
};

VoiceCallDataForm.propTypes = {
  url: PropTypes.string.isRequired,
  transfer_from: PropTypes.string,
  voice_settings: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  has_not_api_key: PropTypes.bool.isRequired
};

const VoiceCallLink = () => {
  const queryClient = useQueryClient();
  const queryKey = ['voice-call/settings'];

  const query = useQuery({ queryKey });

  const { mutate: acceptPolicy } = useMutation({
    mutationFn: acceptPolicyApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  if (query.isLoading) {
    return <Placeholder items={1} subitems={2} />;
  }

  const data = query.data?.data;
  if (!data) { return null; }

  return (
    <PageWrapper
      center
      half
      header="Voice Call over IP"
      prevAction={<BackButton to="/channels" />}
    >
      <Flex col center>
        <OneHalfLayout>
          {!data.has_given_data_consent ? (
            <PolicyAccept
              handlePolicyAccept={acceptPolicy}
              policyUrl="https://sopranodesign.com/privacy/"
            />
          ) : (
              <Panel divider header="Connect your chatbot to Voice Call over IP">
              <Explainer mb>
                Copy the link below into your Voice Call AI platform. Follow our{' '}
                <Link to="/docs/2151317744/2223964202">documentation</Link>.
              </Explainer>

              <VoiceCallDataForm {...data} />
            </Panel>
          )}
        </OneHalfLayout>
      </Flex>
    </PageWrapper>
  );
};

export default VoiceCallLink;